import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  Typography,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
} from "@mui/material";

import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";

export default function LawyerForm({
  onSuccess,
  addLawyer,
  editLawyer,
  setError,
  editData = null,
  onCancel,
}) {
  const decreeFileInputRef = useRef(null);
  const arcaFileInputRef = useRef(null);

  const getInitialForm = () => ({
    name: "",
    lastname: "",
    enrollment: "",
    document: "",
    phone: "",
    address: "",
    tax_condition: "",
    password: "",
    number_decree: "",
    date_decree: "",
  });

  const [form, setForm] = useState(getInitialForm());
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState({
    decree_file: null,
    arca_certificate_file: null,
  });

  const resetForm = useCallback(() => {
    setForm(getInitialForm());
    setSelectedFiles({
      decree_file: null,
      arca_certificate_file: null,
    });
    setErrors({});
    setShowPassword(false);

    if (decreeFileInputRef.current) decreeFileInputRef.current.value = "";
    if (arcaFileInputRef.current) arcaFileInputRef.current.value = "";
  }, []);

  useEffect(() => {
    if (editData) {
      setForm({
        name: editData.name || "",
        lastname: editData.lastname || "",
        enrollment: editData.enrollment || "",
        document: editData.document || "",
        phone: editData.phone || "",
        address: editData.address || "",
        tax_condition: editData.tax_condition || "",
        password: "",
        number_decree: editData.number_decree || "",
        date_decree: editData.date_decree || "",
      });
    } else {
      resetForm();
    }

    setSelectedFiles({
      decree_file: null,
      arca_certificate_file: null,
    });

    if (decreeFileInputRef.current) decreeFileInputRef.current.value = "";
    if (arcaFileInputRef.current) arcaFileInputRef.current.value = "";

    setErrors({});
  }, [editData, resetForm]);

  const validate = () => {
    const temp = {};

    temp.name = form.name?.trim() ? "" : "Nombre requerido";
    temp.lastname = form.lastname?.trim() ? "" : "Apellido requerido";
    temp.enrollment = form.enrollment?.trim() ? "" : "Usuario SAC requerido";

    if (!editData || form.password) {
      if (!form.password) {
        temp.password = "Contraseña requerida";
      } else if (form.password.length < 6) {
        temp.password = "Mínimo 6 caracteres";
      }
    }

    if (
      form.tax_condition &&
      !["monotributista", "responsable_inscripto", "no_inscripto"].includes(
        form.tax_condition,
      )
    ) {
      temp.tax_condition = "Condición tributaria inválida";
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFileChange = (field) => (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedFiles((prev) => ({
        ...prev,
        [field]: null,
      }));
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension !== "pdf") {
      setErrors((prev) => ({
        ...prev,
        [field]: "Solo se permiten archivos .pdf",
      }));

      setSelectedFiles((prev) => ({
        ...prev,
        [field]: null,
      }));

      if (field === "decree_file" && decreeFileInputRef.current) {
        decreeFileInputRef.current.value = "";
      }

      if (field === "arca_certificate_file" && arcaFileInputRef.current) {
        arcaFileInputRef.current.value = "";
      }

      return;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    setSelectedFiles((prev) => ({
      ...prev,
      [field]: file,
    }));
  };

  const removeSelectedFile = (field) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [field]: null,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    if (field === "decree_file" && decreeFileInputRef.current) {
      decreeFileInputRef.current.value = "";
    }

    if (field === "arca_certificate_file" && arcaFileInputRef.current) {
      arcaFileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("name", form.name || "");
      formData.append("lastname", form.lastname || "");
      formData.append("enrollment", form.enrollment || "");
      formData.append("document", form.document || "");
      formData.append("phone", form.phone || "");
      formData.append("address", form.address || "");
      formData.append("tax_condition", form.tax_condition || "");
      formData.append("number_decree", form.number_decree || "");
      formData.append("date_decree", form.date_decree || "");

      if (form.password) {
        formData.append("password", form.password);
      }

      if (selectedFiles.decree_file) {
        formData.append("decree_file", selectedFiles.decree_file);
      }

      if (selectedFiles.arca_certificate_file) {
        formData.append(
          "arca_certificate_file",
          selectedFiles.arca_certificate_file,
        );
      }

      const response = editData
        ? await editLawyer(editData.id, formData, true)
        : await addLawyer(formData, true);

      if (response?.data?.status === "success") {
        onSuccess(response.data.message);
        resetForm();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const apiErrors = {};

        Object.keys(error.response.data.errors).forEach((key) => {
          apiErrors[key] = error.response.data.errors[key][0];
        });

        setErrors(apiErrors);
      } else {
        setError("Ocurrió un error inesperado.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFileBlock = ({
    title,
    field,
    file,
    inputRef,
    helperText,
    existingFileLabel,
  }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>

        {editData && existingFileLabel && !file && (
          <Typography variant="body2" color="text.secondary">
            Archivo actual cargado.
          </Typography>
        )}

        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
          fullWidth
        >
          Seleccionar PDF
          <input
            type="file"
            hidden
            ref={inputRef}
            accept="application/pdf"
            onChange={handleFileChange(field)}
          />
        </Button>

        {file && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <PictureAsPdfIcon color="error" />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  wordBreak: "break-word",
                }}
              >
                {file.name}
              </Typography>
            </Box>

            <Button
              variant="text"
              color="inherit"
              onClick={() => removeSelectedFile(field)}
            >
              Quitar archivo seleccionado
            </Button>
          </>
        )}

        {helperText && (
          <FormHelperText error sx={{ m: 0 }}>
            {helperText}
          </FormHelperText>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Nombre"
            name="name"
            value={form.name}
            onChange={handleChange}
            fullWidth
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Apellido"
            name="lastname"
            value={form.lastname}
            onChange={handleChange}
            fullWidth
            error={!!errors.lastname}
            helperText={errors.lastname}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Usuario SAC"
            name="enrollment"
            value={form.enrollment}
            onChange={handleChange}
            fullWidth
            error={!!errors.enrollment}
            helperText={errors.enrollment}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Matrícula"
            name="document"
            value={form.document}
            onChange={handleChange}
            fullWidth
            error={!!errors.document}
            helperText={errors.document}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Teléfono"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            fullWidth
            error={!!errors.phone}
            helperText={errors.phone}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Dirección"
            name="address"
            value={form.address}
            onChange={handleChange}
            fullWidth
            error={!!errors.address}
            helperText={errors.address}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            label="Condición tributaria"
            name="tax_condition"
            value={form.tax_condition}
            onChange={handleChange}
            fullWidth
            error={!!errors.tax_condition}
            helperText={errors.tax_condition}
          >
            <MenuItem value="">SELECCIONAR</MenuItem>
            <MenuItem value="monotributista">MONOTRIBUTISTA</MenuItem>
            <MenuItem value="responsable_inscripto">
              RESPONSABLE INSCRIPTO
            </MenuItem>
            <MenuItem value="no_inscripto">NO INSCRIPTO</MenuItem>
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Número de Decreto"
            name="number_decree"
            value={form.number_decree}
            onChange={handleChange}
            fullWidth
            error={!!errors.number_decree}
            helperText={errors.number_decree}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Fecha de Decreto"
            name="date_decree"
            type="date"
            value={form.date_decree}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={!!errors.date_decree}
            helperText={errors.date_decree}
          />
        </Grid>

        {!editData && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth variant="outlined" error={!!errors.password}>
              <InputLabel>Contraseña</InputLabel>
              <OutlinedInput
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                label="Contraseña"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText>{errors.password}</FormHelperText>
            </FormControl>
          </Grid>
        )}

        <Grid container>
          <Grid size={{ xs: 12, md: 12 }}>
            <Typography variant="h6" gutterBottom sx={{fontWeight: 'bold'}}>
              Archivos PDF
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {renderFileBlock({
              title: "PDF de decreto",
              field: "decree_file",
              file: selectedFiles.decree_file,
              inputRef: decreeFileInputRef,
              helperText: errors.decree_file,
              existingFileLabel: editData?.decree_file,
            })}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {renderFileBlock({
              title: "Constancia de inscripción ARCA",
              field: "arca_certificate_file",
              file: selectedFiles.arca_certificate_file,
              inputRef: arcaFileInputRef,
              helperText: errors.arca_certificate_file,
              existingFileLabel: editData?.arca_certificate_file,
            })}
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
           
            endIcon={!loading ? <SendIcon /> : null}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : editData ? (
              "Actualizar"
            ) : (
              "Registrar"
            )}
          </Button>
        </Grid>

        {editData && (
          <Grid size={{ xs: 12 }}>
            <Button
              variant="outlined"
              fullWidth
             
              onClick={() => {
                resetForm();
                if (onCancel) onCancel();
              }}
              endIcon={<CancelIcon />}
              disabled={loading}
            >
              Cancelar
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
